import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  Renderer2,
  ViewChild
} from '@angular/core';
import {LoadingComponent} from '../models/loading.component';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  lastValueFrom,
  map,
  Observable, of,
  take
} from 'rxjs';
import {ScenarioImageService} from '../services/scenario-image/scenario-image.service';
import {DoorService} from '../services/door/door.service';
import {CustomerService} from '../services/customer/customer.service';
import {ApplicationStateService} from '../services/application.state.service';
import {TrackingService} from '../services/tracking.service';
import {TranslateService} from '@ngx-translate/core';
import {share} from '../utils/device.utils';
import {Router} from '@angular/router';
import {ScenarioType} from "../models/scenario-image";
import {ProductLinkType} from "../../generated/graphql";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {IframeModalComponent} from "../components/iframe-modal.component";
import {Product} from "../models/product";
import {DEFAULT_SHADOW_INPAINTING_ALPHA, VisualizeEngine} from "@door-vision/visualisation-engine";

@Component({
  template: '', styles: [`
    .iframe-modal {
      height: 900px;
    }
  `]
})
export abstract class AbstractVisualizePanelComponent implements AfterViewInit, OnDestroy, LoadingComponent {

  @ViewChild('canvasContainer')
  canvasContainer: ElementRef | undefined;

  canvas: HTMLCanvasElement;

  isLoading$ = combineLatest([this.scenarioImageService.isLoading$, this.visualizeEngine.isVisualizationInProgress()]).pipe(
    map(([scenariosLoading, inProgress]) => scenariosLoading || inProgress),
    distinctUntilChanged(),
    debounceTime(200)
  );

  selectedDoorAtCurrentIndex$ = this.doorService.getSelectedDoorAtCurrentIndex$()

  selectableIndex$ = this.visualizeEngine.getCurrentScenario().pipe(
    map(scenario => scenario?.scenarioImage.corners.map((corner, index) => {
      return corner.index
    }).sort((a: number, b: number) => a - b))
  )

  selectedDoorCornersIndex$ = this.doorService.getSelectedDoorCornersIndex()

  public customerConfig$ = this.customerService.getCustomerConfig$()

  constructor(protected visualizeEngine: VisualizeEngine,
              protected scenarioImageService: ScenarioImageService,
              protected doorService: DoorService,
              protected renderer: Renderer2,
              protected cdRef: ChangeDetectorRef,
              protected customerService: CustomerService,
              public applicationStateService: ApplicationStateService,
              protected trackingService: TrackingService,
              protected translationService: TranslateService,
              protected router: Router,
              private modalService: NgbModal) {
    this.canvas = document.createElement('canvas');
    this.applicationStateService.register(this);
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.visualizeEngine.onEngineError().subscribe((error) => {
      console.warn('on Engine error', error)
      this.router.navigateByUrl('error').then();
    });

    // Wait for the customer config to be loaded before initializing the visualize engine
    this.customerService.getCustomerConfig$().subscribe((customerConfig) => {
      this.visualizeEngine.init({
        drawGlassLayer: customerConfig?.drawGlassLayerInVisualisation,
        enabledShadowProfiles: customerConfig?.enabledShadowProfiles,
        enableShadowInpainting: customerConfig?.enableShadowInpainting,
        shadowInpaintingAlpha: DEFAULT_SHADOW_INPAINTING_ALPHA
      });
      this.onResize();
    })

    this.scenarioImageService.getSelected$().subscribe(scenario => {
      this.visualizeEngine.drawScenario(scenario);
    });

    combineLatest([
      this.doorService.getSelected$(),
    ]).subscribe(([products]) => {
      this.visualizeEngine.drawProducts(products)
    })

    this.applicationStateService.isApplicationLoaded().subscribe(loaded => {
      if (loaded) {
        this.cdRef.detectChanges();
        if (this.canvasContainer) {
          this.canvasContainer.nativeElement.appendChild(this.canvas);
        }
        this.cdRef.detectChanges();

        this.onResize();
      }
    });
  }

  private viewReady = false;

  @HostListener('window:resize')
  onResize(): void {
    try {
      this.canvas.width = 0;
      this.canvas.height = 0;
      this.cdRef.detectChanges();
      this.canvas.width = this.canvas.offsetWidth;
      this.canvas.height = this.canvas.offsetHeight;

      this.visualizeEngine.updateCanvas(this.canvas);
    } catch (e) {
      this.router.navigateByUrl('error').then()
      console.error('Error during onResize handler', e)
    }
  }

  async share(): Promise<void> {
    // Build the sharing data
    const url = window.location.href;
    const title = await lastValueFrom(this.translationService.get('redesign.sharing.title'));
    const text = await lastValueFrom(this.translationService.get('redesign.sharing.text'));
    const copyMessageText = await lastValueFrom(this.translationService.get('redesign.sharing.copyMessage'));
    const sharingData: ShareData = {
      url,
      title,
      text
    };

    await share(sharingData, copyMessageText);

    // Track the sharing action
    this.trackingService.track({event: 'shareScenario'}).then();
  }

  selectedIndex(index: number): void {
    this.doorService.selectDoorCornersIndex(index)
    // TODO somehow highlight the selected door
  }

  toggleFavorite(product: Product): void {
    this.doorService.toggleFavorite(product);
    this.trackingService.track({
      event: 'toggleFavorite',
      body: {
        selectedDoorId: product.id,
        selectedExternalDoorId: product.externalIds[0],
        isFavorite: product.isFavorite,
        location: 'buttonGroup'
      }
    }).then();
  }

  flipDoor(): void {
    const index = this.doorService.getSelectedDoorCornersIndexValue()
    this.visualizeEngine.flipDoor(index);
    this.trackingService.track({event: 'flipDoor'}).then();
  }

  isShowingExampleImage(): boolean {
    return this.visualizeEngine.getCurrentScenarioValue()?.scenarioImage.type === ScenarioType.EXAMPLE;
  }

  refineCorners(): void {
    this.applicationStateService.setRefineMode("EDIT")
  }

  async openHomepage(): Promise<void> {
    const homepageUrl = await this.customerService.getCustomerHomepageUrl().pipe(take(1)).toPromise()
    if (homepageUrl) {
      window.open(homepageUrl, '_blank');
    }
  }

  openProductUrl(product: Product): void {
    this.customerConfig$.pipe(take(1)).subscribe((customerConfig) => {

      // Either use the shop link overwrite or the product page url from the door
      const shopLinkOverwrite = customerConfig.shopLinkOverwrite
      const url = shopLinkOverwrite && shopLinkOverwrite.length !== 0 ? shopLinkOverwrite : product.link

      if (!url) {
        return
      }

      const productPageUrl = url
        .replace(/<EXTERNAL_ID>/g, product.externalIds[0])
        .replace(/<URL>/g, encodeURIComponent(window.location.href))
        .replace(/<DOOR_NAME>/g, encodeURIComponent(product.name))

      this.trackingService.track({
        event: 'productLinkClicked',
        body: {selectedDoorId: product.id, selectedExternalDoorId: product.externalIds[0]}
      }).then();

      switch (customerConfig.productLinkType) {
        case ProductLinkType.NewTab:
          window.open(productPageUrl, '_blank');
          break;
        case ProductLinkType.SameTab:
          window.open(productPageUrl, '_self');
          break;
        case ProductLinkType.PopUp:
          const modal = this.modalService.open(IframeModalComponent, {
            centered: true,
            windowClass: 'iframe-modal',
            scrollable: false,
            fullscreen: "lg"
          })
          modal.componentInstance.url = productPageUrl
          modal.componentInstance.title = product.name
          break;
      }
    })
  }

  isLoaded(): Observable<boolean> {
    // TODO do we still need this?
    return of(true);
  }

  ngOnDestroy(): void {
    this.applicationStateService.unregister(this);
  }
}
